import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { PassAlgarveLogo, PassAlgarveQrMark } from "@/components/PassAlgarveLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SiteFooter from "@/components/SiteFooter";
import HowItWorksSteps from "@/components/HowItWorksSteps";
import SiteHeader from "@/components/SiteHeader";
import PartnerLogos from "@/components/PartnerLogos";
import { toast } from "sonner";
import { loc } from "@/lib/locale";
import Seo, { passportJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import {
  ArrowRight, Check, Star, MapPin, Clock, Calendar, Heart, Mountain,
  Gem, Compass, Users, Crown, ChevronRight, ShieldCheck, QrCode, Sparkles,
  X, ChevronLeft, Award, Percent, Headphones, Lock, BadgeCheck, Zap, Shield,
  CalendarCheck, Tag, CreditCard, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ICON = { explorer: Compass, premium: Gem, family: Users, romantic: Heart, adventure: Mountain };
const CARD_3D_IMAGE = "https://customer-assets.emergentagent.com/job_f049ee2b-edae-4cc4-8e76-bfc796e90f9f/artifacts/uzxdncv1_card-pass-banner.png";

const TIER_HERO = {
  explorer: "https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=1920&q=85",
  premium: "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=1920&q=85",
  family: "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1920&q=85",
  romantic: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=85",
  adventure: "https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?w=1920&q=85",
};

const TESTIMONIALS = [
  { name: "Lucas e Marina", countryKey: "pd.tc1", flag: "🇧🇷", textKey: "pd.tm1", avatar: "https://images.unsplash.com/photo-1521119989659-a83eee488004?w=200&q=80" },
  { name: "Camille Dupont", countryKey: "pd.tc2", flag: "🇫🇷", textKey: "pd.tm2", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80" },
  { name: "James Smith", countryKey: "pd.tc3", flag: "🇬🇧", textKey: "pd.tm3", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80" },
  { name: "Carlos e Ana", countryKey: "pd.tc4", flag: "🇪🇸", textKey: "pd.tm4", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80" },
];

// ============================================================
// Lightbox (kept for gallery thumbnails) — currently unused by new layout
// ============================================================
const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, onPrev, onNext]);

  if (index === null) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white p-2"><X className="w-6 h-6" /></button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 rounded-full p-2"><ChevronLeft className="w-6 h-6" /></button>
      <img src={images[index]} alt="" className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 rounded-full p-2"><ChevronRight className="w-6 h-6" /></button>
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm bg-black/40 px-3 py-1 rounded-full">{index + 1} / {images.length}</p>
    </div>
  );
};

export default function PassportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [pt, setPt] = useState(null);
  const [allAttractions, setAllAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [showFloatBuy, setShowFloatBuy] = useState(false);
  const [qty, setQty] = useState(1);
  const [holderNames, setHolderNames] = useState([]);
  const buyMarkerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!buyMarkerRef.current) return;
      setShowFloatBuy(buyMarkerRef.current.getBoundingClientRect().top < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [loading]);

  useEffect(() => {
    (async () => {
      try {
        const [allRes, attrRes] = await Promise.all([
          axios.get(`${API}/passport-types`),
          axios.get(`${API}/attractions`),
        ]);
        const found = (allRes.data || []).find(x =>
          x.id === id ||
          (x.slug && x.slug === id) ||
          (x.slug_en && x.slug_en === id) ||
          (Array.isArray(x.old_slugs) && x.old_slugs.includes(id)) ||
          (Array.isArray(x.old_slugs_en) && x.old_slugs_en.includes(id))
        );
        setPt(found || null);
        setAllAttractions(attrRes.data || []);
      } catch {
        toast.error(t("pd.loadError"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Canonical URL per language: redirects old slugs and normalizes the path (301-equivalent for SPA)
  useEffect(() => {
    if (!pt) return;
    const ptP = `/passaporte/${pt.slug || pt.id}`;
    const enP = `/en/passport/${pt.slug_en || pt.slug || pt.id}`;
    const expected = language === "en" ? enP : ptP;
    if (routerLocation.pathname !== expected) {
      navigate(expected + routerLocation.search + routerLocation.hash, { replace: true });
    }
  }, [pt, language, routerLocation.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuy = async () => {
    if (!user) { navigate("/register"); return; }
    setBuying(true);
    try {
      const r = await axios.post(`${API}/checkout/create-session`,
        { origin_url: window.location.origin, passport_type_id: pt.id, quantity: qty, holder_names: holderNames.slice(0, qty) },
        { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = r.data.url;
    } catch {
      toast.error(t("pd.checkoutError"));
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071A35] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4A85D]"></div>
      </div>
    );
  }

  if (!pt) {
    return (
      <div className="min-h-screen bg-[#071A35] flex flex-col items-center justify-center text-white p-6">
        <p className="text-xl mb-4">{t("pd.notFound")}</p>
        <Link to="/" className="text-[#D4A85D] hover:underline">{t("pd.backHome")}</Link>
      </div>
    );
  }

  const Icon = TIER_ICON[pt.tier] || Crown;
  const heroImg = pt.image_url || TIER_HERO[pt.tier] || TIER_HERO.explorer;
  const includedAttractions = (pt.attractions || [])
    .map(aid => allAttractions.find(a => a.id === aid))
    .filter(Boolean);
  const hasDiscount = pt.discount_amount > 0 && pt.sum_total > 0;

  const qtySelector = (suffix) => (
    <>
      <div className="flex items-center justify-between bg-[#071A35] rounded-xl px-4 py-2.5 mb-3 border border-[#D4A85D]/20">
        <span className="text-white/70 text-[11px] uppercase tracking-wider">{t("pd.qtyLabel")}</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="w-8 h-8 rounded-full border border-[#D4A85D]/40 text-[#D4A85D] font-bold text-lg leading-none hover:bg-[#D4A85D]/10"
            data-testid={`qty-minus-${suffix}`}
          >−</button>
          <span className="text-white font-bold text-lg w-6 text-center" data-testid={`qty-value-${suffix}`}>{qty}</span>
          <button
            type="button"
            onClick={() => setQty((q) => Math.min(10, q + 1))}
            className="w-8 h-8 rounded-full border border-[#D4A85D]/40 text-[#D4A85D] font-bold text-lg leading-none hover:bg-[#D4A85D]/10"
            data-testid={`qty-plus-${suffix}`}
          >+</button>
        </div>
      </div>
      {qty > 1 && (
        <p className="text-center text-white/80 text-xs mb-3">
          {t("pd.total")}: <span className="text-[#D4A85D] font-bold text-base">€{Math.round(pt.price * qty)}</span> ({qty}x €{Math.round(pt.price)})
        </p>
      )}
      {qty > 1 && (
        <div className="mb-3 space-y-2">
          <p className="text-white/60 text-[10px] uppercase tracking-wider">{t("pd.holderNamesLabel")}</p>
          {Array.from({ length: qty }).map((_, i) => (
            <input
              key={i}
              type="text"
              maxLength={60}
              value={holderNames[i] || ""}
              onChange={(e) => setHolderNames((prev) => { const nx = [...prev]; nx[i] = e.target.value; return nx; })}
              placeholder={`${t("pd.holderPlaceholder")} ${i + 1}`}
              className="w-full bg-[#071A35] border border-[#D4A85D]/25 focus:border-[#D4A85D]/60 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 outline-none"
              data-testid={`holder-name-input-${suffix}-${i}`}
            />
          ))}
        </div>
      )}
    </>
  );

  const shortTitle = (loc(pt, "name", language) || "").replace(/PASS\s*/i, "").toUpperCase();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
      <Seo
        title={language === "en"
          ? (pt.seo_title_en || `${pt.name_en || pt.name} | Algarve Experiences Pass`)
          : (pt.seo_title || `${pt.name} | Passe de Experiências no Algarve`)}
        description={language === "en"
          ? (pt.seo_description_en || loc(pt, "description", language) || loc(pt, "tagline", language))
          : (pt.seo_description || loc(pt, "description", language) || loc(pt, "tagline", language))}
        image={(language === "en" ? (pt.social_image_en || pt.social_image) : pt.social_image) || pt.image_url}
        keywords={language === "en" ? pt.seo_keyword_en : pt.seo_keyword}
        ptPath={`/passaporte/${pt.slug || pt.id}`}
        enPath={`/en/passport/${pt.slug_en || pt.slug || pt.id}`}
        noindex={pt.indexable === false}
        type="product"
        jsonLd={[passportJsonLd(pt, language === "en"
          ? `/en/passport/${pt.slug_en || pt.slug || pt.id}`
          : `/passaporte/${pt.slug || pt.id}`, language === "en"), breadcrumbJsonLd([
          { name: "PASS Algarve", path: "/" },
          { name: language === "en" ? "Passports" : "Passaportes", path: "/#passport" },
          { name: loc(pt, "name", language), path: language === "en" ? `/passport/${pt.slug_en || pt.slug || pt.id}` : `/passaporte/${pt.slug || pt.id}` },
        ], language === "en")]}
      />
      <SiteHeader active={t("nav.passports")} onBuyClick={handleBuy} />

      {/* ============== HERO ============== */}
      <section className="relative pt-20" data-testid="passport-detail-hero">
        <div className="relative min-h-[400px] lg:h-[580px] overflow-hidden">
          <img src={heroImg} alt={pt.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071A35] via-[#071A35]/70 to-transparent" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full py-8 lg:py-0 flex flex-col justify-center">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-6" data-testid="breadcrumb">
              <Link to="/" className="hover:text-[#D4A85D]">{t("pd.home")}</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/#passport" className="hover:text-[#D4A85D]">{t("pd.passports")}</Link>
              <span className="hidden sm:flex items-center gap-2 min-w-0">
                <ChevronRight className="w-3 h-3 flex-shrink-0" />
                <span className="text-[#D4A85D] font-semibold truncate">{(loc(pt, "name", language) || "").toUpperCase()}</span>
              </span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-8 items-center w-full">
              {/* LEFT: title + description + rating */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7 text-white">
                <p className="text-white text-2xl lg:text-3xl font-bold tracking-tight">PASS</p>
                <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-[#D4A85D] leading-[1.05] tracking-tight mb-4 lg:mb-5 break-words">{shortTitle}</h1>
                <p className="text-white/85 text-sm sm:text-base lg:text-lg max-w-xl leading-relaxed mb-6">
                  {loc(pt, "description", language) || loc(pt, "tagline", language) || t("pd.defaultDesc")}
                </p>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#D4A85D]/20 border border-[#D4A85D] flex items-center justify-center">
                    <Star className="w-4 h-4 text-[#D4A85D] fill-[#D4A85D]" />
                  </div>
                  <span className="text-white font-bold text-lg">4.9</span>
                  <div className="flex gap-0.5 text-[#D4A85D]">
                    {[1,2,3,4,5].map(n => <Star key={n} className="w-4 h-4 fill-current" />)}
                  </div>
                  <span className="text-white/60 text-sm">{t("pd.reviewsCount")}</span>
                </div>
              </motion.div>

              {/* RIGHT: pricing card */}
              <motion.aside
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="lg:col-span-5 hidden lg:block"
                data-testid="hero-price-card"
              >
                <div className="bg-[#0a1f3f]/95 backdrop-blur-md border border-[#D4A85D]/30 rounded-2xl p-8 shadow-2xl max-w-md ml-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-white font-bold text-2xl leading-tight">PASS</p>
                      <p className="text-[#D4A85D] font-bold text-sm tracking-widest">ALGARVE</p>
                      <p className="text-white font-bold text-xl mt-1">{shortTitle}</p>
                    </div>
                    <div className="w-14 h-14 flex items-center justify-center overflow-hidden" data-testid="card-qr-mark">
                      <PassAlgarveQrMark size={46} />
                    </div>
                  </div>
                  <p className="text-white/55 text-[10px] uppercase tracking-widest text-center mb-1">{t("pd.from")}</p>
                  <p className="text-white font-bold text-6xl text-center leading-none">€{Math.round(pt.price)}</p>
                  <p className="text-white/55 text-xs text-center mt-1 mb-5">{t("pd.perPerson")}</p>
                  {hasDiscount && (
                    <p className="text-center text-emerald-400 text-xs font-semibold mb-3">
                      {t("pd.save")} €{pt.discount_amount.toFixed(0)} ({Math.round(pt.discount_percent)}%) {t("pd.vsSeparate")}
                    </p>
                  )}
                  {qtySelector("desktop")}
                  <Button
                    onClick={handleBuy}
                    disabled={buying}
                    className="w-full bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md py-6 text-sm uppercase tracking-wider shadow-xl mb-5"
                    data-testid="hero-buy-btn"
                  >
                    {buying ? t("pd.processing") : t("pd.buyNow")}
                  </Button>
                  <div className="bg-[#071A35] rounded-xl p-3 flex items-center gap-3 mb-3">
                    <BadgeCheck className="w-5 h-5 text-[#D4A85D] flex-shrink-0" />
                    <p className="text-white/80 text-xs leading-snug">{t("pd.receiveInstant")}</p>
                  </div>
                  <div className="flex items-center justify-around text-white/60 text-[11px]">
                    <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#D4A85D]" /> {t("pd.securePay")}</span>
                    <span className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-[#D4A85D]" /> {t("pd.flexCancel")}</span>
                  </div>
                </div>
              </motion.aside>
            </div>
          </div>
        </div>

        {/* feature pills bar (right under hero, on the dark image bottom) — desktop only */}
        <div className="hidden lg:block bg-[#071A35] border-t border-[#D4A85D]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-around gap-3 text-white/85">
            {[
              { Icon: CalendarCheck, label: t("pd.pill1") },
              { Icon: QrCode, label: t("pd.pill2") },
              { Icon: Sparkles, label: t("pd.pill3") },
              { Icon: Users, label: t("pd.pill4") },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs uppercase tracking-wider">
                <f.Icon className="w-4 h-4 text-[#D4A85D]" />
                <span>{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== MOBILE PRICING CARD (right under hero) ============== */}
      <section className="lg:hidden bg-white px-4 pt-6 pb-2" data-testid="mobile-price-card-section">
        <div className="bg-[#0a1f3f] border border-[#D4A85D]/30 rounded-2xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-white font-bold text-xl leading-tight">PASS</p>
              <p className="text-[#D4A85D] font-bold text-xs tracking-widest">ALGARVE</p>
              <p className="text-white font-bold text-lg mt-1">{shortTitle}</p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center overflow-hidden" data-testid="card-qr-mark-mobile">
              <PassAlgarveQrMark size={40} />
            </div>
          </div>
          <p className="text-white/55 text-[10px] uppercase tracking-widest text-center mb-1">{t("pd.from")}</p>
          <p className="text-white font-bold text-5xl text-center leading-none">€{Math.round(pt.price)}</p>
          <p className="text-white/55 text-xs text-center mt-1 mb-4">{t("pd.perPerson")}</p>
          {hasDiscount && (
            <p className="text-center text-emerald-400 text-xs font-semibold mb-3">
              {t("pd.save")} €{pt.discount_amount.toFixed(0)} ({Math.round(pt.discount_percent)}%) {t("pd.vsSeparate")}
            </p>
          )}
          {qtySelector("mobile")}
          <Button
            onClick={handleBuy}
            disabled={buying}
            className="w-full bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md py-6 text-sm uppercase tracking-wider shadow-xl mb-4"
            data-testid="hero-buy-btn-mobile"
          >
            {buying ? t("pd.processing") : t("pd.buyNow")}
          </Button>

          {/* mobile-only feature list (was the pills bar above) */}
          <ul className="space-y-2.5 mb-4">
            {[
              { Icon: CalendarCheck, label: t("pd.pill1") },
              { Icon: Sparkles, label: t("pd.pill3") },
              { Icon: Users, label: t("pd.pill4") },
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-white/85 text-xs uppercase tracking-wider">
                <f.Icon className="w-4 h-4 text-[#D4A85D] flex-shrink-0" />
                <span>{f.label}</span>
              </li>
            ))}
          </ul>

          <div className="bg-[#071A35] rounded-xl p-3 flex items-center gap-3 mb-3">
            <BadgeCheck className="w-5 h-5 text-[#D4A85D] flex-shrink-0" />
            <p className="text-white/80 text-xs leading-snug">{t("pd.receiveInstant")}</p>
          </div>
          <div className="flex items-center justify-around text-white/60 text-[11px]">
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#D4A85D]" /> {t("pd.securePay")}</span>
            <span className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-[#D4A85D]" /> {t("pd.flexCancel")}</span>
          </div>
        </div>
      </section>
      <div ref={buyMarkerRef} aria-hidden="true" />

      {/* ============== O QUE ESTÁ INCLUÍDO ============== */}
      <section className="py-16 lg:py-20 bg-white" data-testid="included-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#071A35] uppercase tracking-[0.15em] mb-10">
            {t("pd.includedTitle")}
          </h2>
          <div className="max-w-5xl">
            {/* list */}
            <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
              {(includedAttractions.length > 0
                ? includedAttractions.map((a) => loc(a, "name", language))
                : (pt.benefits && pt.benefits.length > 0 ? pt.benefits : [
                    t("pd.db1"), t("pd.db2"), t("pd.db3"), t("pd.db4"), t("pd.db5"), t("pd.db6"), t("pd.db7"),
                  ])
              ).map((b, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
                  <div className="w-7 h-7 rounded-full border-2 border-[#D4A85D] bg-[#D4A85D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#D4A85D]" strokeWidth={3} />
                  </div>
                  <p className="text-[#071A35] text-base leading-relaxed">{b}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============== ATRAÇÕES INCLUÍDAS (dark) ============== */}
      <section id="experiences" className="py-16 lg:py-20 bg-[#071A35]" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-white uppercase tracking-[0.15em]">
              {t("pd.attrTitle")}
            </h2>
            <a href="#all-experiences" className="hidden sm:flex items-center gap-2 text-[#D4A85D] text-xs uppercase tracking-widest hover:gap-3 transition-all">
              {t("pd.viewAll")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {includedAttractions.length === 0 ? (
            <div className="text-center text-white/40 py-12">{t("pd.noAttr")}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {includedAttractions.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-[#0a2547] rounded-2xl overflow-hidden border border-[#D4A85D]/15 hover:border-[#D4A85D]/40 transition-all"
                  data-testid={`experience-card-${a.id}`}
                >
                  <Link to={`/experiencias/${a.slug || a.id}`} className="block" data-testid={`experience-link-${a.id}`}>
                  <div className="aspect-[4/3] relative overflow-hidden bg-[#071A35]">
                    {a.image_url ? (
                      <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-10 h-10 text-[#D4A85D]/40" />
                      </div>
                    )}
                    {/* top badges */}
                    <div className="absolute top-2 left-2 flex gap-1.5">
                      <span className="bg-[#071A35]/90 text-[#D4A85D] text-[10px] font-bold px-2 py-1 rounded-md flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-current" /> 4.9
                      </span>
                      {a.duration && (
                        <span className="bg-[#071A35]/90 text-white text-[10px] font-bold px-2 py-1 rounded-md flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> {a.duration}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-white text-sm mb-1 leading-tight">{loc(a, "name", language)}</h3>
                    <p className="flex items-center gap-1 text-white/50 text-[10px] mb-2">
                      <MapPin className="w-3 h-3 text-[#D4A85D]" /> Lagos
                    </p>
                    <p className="text-white/65 text-[11px] leading-snug line-clamp-2 mb-3 min-h-[28px]">
                      {loc(a, "description", language) || t("pd.attrFallback")}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <span className="bg-[#D4A85D] text-[#071A35] text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wider">{t("pd.included")}</span>
                      {a.price_total > 0 && (
                        <span className="text-white/60 text-[11px]">{t("pd.value")} <span className="text-white font-bold">€{Math.round(a.price_total)}</span></span>
                      )}
                    </div>
                  </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* secondary feature pills */}
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Zap, label: t("pd.sp1") },
              { Icon: Users, label: t("pd.sp2") },
              { Icon: Percent, label: t("pd.sp3") },
              { Icon: Headphones, label: t("pd.sp4") },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-white/80">
                <div className="w-10 h-10 rounded-full border border-[#D4A85D]/30 bg-[#D4A85D]/10 flex items-center justify-center flex-shrink-0">
                  <f.Icon className="w-4 h-4 text-[#D4A85D]" />
                </div>
                <span className="text-xs uppercase tracking-wider font-semibold">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== BENEFÍCIOS + COMO FUNCIONA ============== */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl lg:text-2xl font-bold text-[#071A35] uppercase tracking-[0.15em] mb-10 text-center">
            {t("pd.howTitle")}
          </h2>
          <HowItWorksSteps />
        </div>
      </section>

      {/* ============== GUARANTEES BAR ============== */}
      <section className="bg-[#071A35] py-10" data-testid="guarantees-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6 text-white">
          {[
            { Icon: CalendarCheck, title: t("pd.g1t"), desc: t("pd.g1d") },
            { Icon: RefreshCw, title: t("pd.g2t"), desc: t("pd.g2d") },
            { Icon: Shield, title: t("pd.g3t"), desc: t("pd.g3d") },
            { Icon: BadgeCheck, title: t("pd.g4t"), desc: t("pd.g4d") },
          ].map((g, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full border border-[#D4A85D]/30 bg-[#D4A85D]/10 flex items-center justify-center flex-shrink-0">
                <g.Icon className="w-4 h-4 text-[#D4A85D]" />
              </div>
              <div>
                <p className="font-bold text-xs uppercase tracking-wider mb-1">{g.title}</p>
                <p className="text-white/65 text-xs leading-relaxed">{g.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== TESTIMONIALS ============== */}
      <section className="py-16 lg:py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#071A35] uppercase tracking-[0.15em]">
              {t("pd.testTitle")}
            </h2>
            <a href="#all-reviews" className="hidden sm:flex items-center gap-2 text-[#D4A85D] text-xs uppercase tracking-widest hover:gap-3 transition-all">
              {t("pd.moreReviews")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((tm, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#071A35]/10 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <img src={tm.avatar} alt={tm.name} className="w-12 h-12 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1 text-[#D4A85D] mb-1">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <span className="text-[#071A35] font-bold text-sm">5.0</span>
                    </div>
                    <p className="text-[#071A35]/80 text-xs italic leading-relaxed line-clamp-3">"{t(tm.textKey)}"</p>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-[#071A35]/10 pt-3">
                  <div>
                    <p className="text-[#071A35] font-bold text-xs">{tm.name}</p>
                    <p className="text-[#071A35]/60 text-[11px]">{t(tm.countryKey)}</p>
                  </div>
                  <span className="text-xl">{tm.flag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PARTNERS ============== */}
      <section className="py-12 bg-white" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-[#071A35] font-bold uppercase tracking-[0.15em] text-sm mb-8">{t("pd.partnersTitle")}</h3>
          <PartnerLogos />
        </div>
      </section>

      {/* ============== FINAL CTA (moved to home) ============== */}

      {/* Full site footer */}
      <SiteFooter />

      {/* Floating buy bar */}
      {showFloatBuy && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#071A35] border-t border-[#D4A85D]/30 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
          data-testid="floating-buy-bar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate">{loc(pt, "name", language)}</p>
              <p className="text-white/60 text-xs">
                <span className="text-[#D4A85D] font-bold text-lg">€{Math.round(pt.price * qty)}</span>{qty > 1 ? ` (${qty}x)` : ` / ${t("pd.perPerson")}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full border border-[#D4A85D]/40 text-[#D4A85D] font-bold text-lg leading-none"
                data-testid="qty-minus-float"
              >−</button>
              <span className="text-white font-bold w-5 text-center" data-testid="qty-value-float">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(10, q + 1))}
                className="w-8 h-8 rounded-full border border-[#D4A85D]/40 text-[#D4A85D] font-bold text-lg leading-none"
                data-testid="qty-plus-float"
              >+</button>
            </div>
            <Button
              onClick={handleBuy}
              disabled={buying}
              className="bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold rounded-md px-6 sm:px-10 py-5 text-xs sm:text-sm uppercase tracking-wider flex-shrink-0"
              data-testid="floating-buy-btn"
            >
              {buying ? t("pd.processing") : t("pd.buyNow")}
            </Button>
          </div>
        </motion.div>
      )}

      <Lightbox images={[]} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onPrev={() => {}} onNext={() => {}} />
    </div>
  );
}
